import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Context as AuthContext } from '../../../../../contexts/AuthContext';
import PerformanceFeedback from '../../../../../services/PerformanceFeedback';
import FeedbackCard from './FeedbackCard';
import SummaryModalRecordRow from '../components/SummaryModalRecordRow';

interface FeedbackData {
  id: number;
  name: string;
  date_of_incident: string;
  position: string;
  department: string;
  supervisor: string;
  date_of_hire: string;
  policy_violation: string;
  description_of_feedback: string;
  immediate_feedback: string;
  expectation_for_improvement: string;
  email_of_feedback: string;
  created_at: string;
  updated_at: string;
}

interface FeedbackItem {
  id?: number;
  date_of_incident?: string;
  dateOfIncident?: string;
  type?: string;
  policy_violated?: string;
  policy_violation?: string;
  policyViolations?: { name?: string }[];
  policyViolation?: { name?: string };
  name?: string;
  position?: string;
  department?: string;
  supervisor?: string;
  date_of_hire?: string;
  dateOfHire?: string;
  description_of_feedback?: string;
  descriptionOfFeedback?: string;
  immediate_feedback?: string;
  immediateFeedback?: string;
  expectation_for_improvement?: string;
  expectationForImprovement?: string;
  email_of_feedback?: string;
  emailOfFeedback?: string;
  created_at?: string;
  createdAt?: string;
  data?: FeedbackData;
}

interface FeedbackSummaryModalProps {
  isOpen: boolean;
  toggle: () => void;
  userId?: string | null;
  userName?: string;
  feedbackData?: FeedbackItem[];
}

const getFeedbackPolicyViolationText = (feedback: FeedbackItem) => {
  if (feedback.policyViolations && Array.isArray(feedback.policyViolations)) {
    const joined = feedback.policyViolations.map((pv: any) => pv.name || pv).join(', ');
    if (joined) {
      return joined;
    }
  }
  return feedback.policy_violated ?? feedback.policyViolation?.name ?? feedback.policy_violation ?? '';
};

const FeedbackSummaryModal = ({ isOpen, toggle, userId, userName, feedbackData }: FeedbackSummaryModalProps) => {
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { token }: any = useContext(AuthContext);

  const loadFeedbacks = useCallback(async () => {
    if (!userId || !token) {
      return;
    }

    try {
      setIsLoading(true);
      const service = PerformanceFeedback(token);
      const response = await service.getUserFeedbacks(userId, 'feedback');

      const feedbackDataResponse = response?.data || [];
      setFeedbacks(feedbackDataResponse);
    } catch (error: any) {
      console.error('Error loading feedbacks:', error);
      toast.error(error?.response?.data?.message || 'Error loading feedbacks');
      setFeedbacks([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFeedback(null);
      if (feedbackData) {
        setFeedbacks(feedbackData);
      } else if (userId) {
        loadFeedbacks();
      }
    }
  }, [isOpen, userId, feedbackData, loadFeedbacks]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return moment.utc(dateString).format('MM/DD/YYYY');
  };

  const handleClose = () => {
    setSelectedFeedback(null);
    setFeedbacks([]);
    toggle();
  };

  const renderDetailCard = (feedback: FeedbackItem, index: number) => (
    <FeedbackCard
      key={feedback.id ?? index}
      index={index}
      name={feedback.name ?? ''}
      dateOfIncident={formatDate(feedback.date_of_incident ?? feedback.dateOfIncident)}
      position={feedback.position ?? ''}
      department={feedback.department ?? ''}
      supervisor={feedback.supervisor ?? ''}
      dateOfHire={formatDate(feedback.date_of_hire ?? feedback.dateOfHire)}
      policyViolation={getFeedbackPolicyViolationText(feedback)}
      immediateFeedback={String(feedback.immediate_feedback ?? feedback.immediateFeedback ?? '')}
      emailOfFeedback={String(feedback.email_of_feedback ?? feedback.emailOfFeedback ?? '')}
      descriptionOfFeedback={String(feedback.description_of_feedback ?? feedback.descriptionOfFeedback ?? '')}
      expectationForImprovement={String(
        feedback.expectation_for_improvement ?? feedback.expectationForImprovement ?? '',
      )}
      createdAt={formatDate(feedback.created_at ?? feedback.createdAt)}
    />
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      {selectedFeedback ? (
        <div className="modal-header align-items-start">
          <button
            type="button"
            className="btn btn-link p-0 mr-3 text-dark mt-1"
            onClick={() => setSelectedFeedback(null)}
            aria-label="Back"
          >
            <i className="fas fa-arrow-left fa-lg" />
          </button>
          <div className="flex-grow-1">
            <h5 className="mb-0 modal-title">Feedback</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
          <button type="button" className="close" onClick={handleClose} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ) : (
        <ModalHeader toggle={handleClose} tag="div">
          <div>
            <h5 className="mb-0 modal-title">Feedback</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
        </ModalHeader>
      )}
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', paddingTop: selectedFeedback ? '1rem' : '0' }}>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : selectedFeedback ? (
          <div className="p-1">{renderDetailCard(selectedFeedback, 0)}</div>
        ) : (
          <>
            {feedbacks.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="fas fa-inbox fa-3x mb-3" />
                <p>No feedback records found</p>
              </div>
            ) : (
              <div className="pt-2">
                {feedbacks.map((feedback, index) => (
                  <SummaryModalRecordRow
                    key={feedback.id ?? index}
                    dateText={formatDate(feedback.date_of_incident ?? feedback.dateOfIncident)}
                    description={getFeedbackPolicyViolationText(feedback) || '—'}
                    onClick={() => setSelectedFeedback(feedback)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default FeedbackSummaryModal;
